import { useMutation, useQueryClient } from '@tanstack/react-query';

import { handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
	AlertSeverityEnum,
	UseMutationsParams,
} from '@/shared/interfaces';
import { ApiResponse } from '@/shared/interfaces/erp/global/response-axios.interface';
import { useUiStore } from '@/store/ui';

const { post, put } = erpApi();

const BASE_URL = `${urlBackend}api/v1/orden-servicio`;

export type VisitaSaveForm = {
	id_cliente: string;
	area_destino_id: string;
	id_zona: string;
	pais: string;
	provincia: string;
	ciudad: string;
	parroquia: string;
	zona: string;
	direccion: string;
	observaciones?: string;
};

export type VisitaUpdateForm = {
	observaciones?: string;
	fecha_visita?: string; // ISO-8601 format: "2026-05-11T00:01:00"
};

export type TrasladoSaveForm = {
	pais: string;
	provincia: string;
	ciudad: string;
	parroquia: string;
	zona: string;
	sector: string;
	direccion: string;
	coordenada: string;
	// Agendamiento del traslado (planificador): flota + fecha/hora ISO.
	flota?: string;
	fecha_traslado?: string; // ISO "YYYY-MM-DDTHH:mm:00"
	observaciones?: string;
	// Detalle de valores.
	valor_traslado?: string;
	traslado_por_fibrapoints?: boolean;
	recaudacion_tecnico?: boolean;
	cargo_a_factura?: boolean;
	meses_pago?: number | string;
	id_contrato_softnet?: string;
	puntos_antes_canje?: number;
	puntos_canjeados?: number;
};

export type OrdenServicioSaveForm = {
	id_ticket: string;
	tipo_orden_servicio: 'VISITA' | 'TRASLADO' | 'NO_REQUIERE';
	user_create: string;
	contrato: string;
	id_instalacion_externa: string | number;
	visita?: VisitaSaveForm;
	traslado?: TrasladoSaveForm;
};

export type OrdenServicioResponse = {
	id_orden_servicio: string;
	id_ticket: string;
	tipo_orden_servicio: string;
	user_create: string;
	contrato: string;
	visita?: VisitaSaveForm & { id_visita?: string; id_orden_servicio?: string };
	traslado?: TrasladoSaveForm & { id_traslado?: string; id_orden_servicio?: string };
	fechaCreacion?: string;
	fechaModificacion?: string;
};



export const useCreateOrdenServicio = ({
	navigate,
	enableRedirect = true,
	returnUrl,
	returnErrorUrl,
	enableErrorRedirect = false,
	customMessageErrorAlert,
	enableSuccessAlert = true,
	customSuccessMessage,
}: UseMutationsParams) => {
	const queryClient = useQueryClient();
	const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
	const setAlert = useUiStore.getState().setAlert;

	return useMutation({
		mutationFn: createOrdenServicio,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['orden-servicio'] });

			enableRedirect && navigate && returnUrl && navigate(returnUrl);

			enableSuccessAlert &&
				setAlert &&
				setAlert(
					true,
					customSuccessMessage ?? 'Orden de servicio creada correctamente',
					AlertSeverityEnum.success
				);
		},
		onError: error => {
			enableErrorRedirect &&
				navigate &&
				returnUrl &&
				navigate(returnErrorUrl ?? (returnUrl || ''));

			handleAxiosError(error, customMessageErrorAlert);
		},
		onSettled: () => {
			setIsGlobalLoading(false);
		},
	});
};
export const createOrdenServicio = async (data: OrdenServicioSaveForm) => {
	const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
	setIsGlobalLoading(true);

	return post<ApiResponse<OrdenServicioResponse>>(BASE_URL, data, true);
};

export const useUpdateVisita = ({
	navigate,
	enableRedirect = true,
	returnUrl,
	returnErrorUrl,
	enableErrorRedirect = false,
	customMessageErrorAlert,
	enableSuccessAlert = true,
	customSuccessMessage,
	ticketId,
}: UseMutationsParams & { ticketId?: string }) => {
	const queryClient = useQueryClient();
	const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
	const setAlert = useUiStore.getState().setAlert;

	return useMutation({
		mutationFn: ({ idVisita, data }: { idVisita: string; data: VisitaUpdateForm }) =>
			updateVisita(idVisita, data),
		onSuccess: () => {
			// Invalidate the specific ticket query if ticketId is provided
			if (ticketId) {
				queryClient.invalidateQueries({ queryKey: ['gestion-tickets', ticketId] });
			} else {
				queryClient.invalidateQueries({ queryKey: ['visita'] });
			}

			enableRedirect && navigate && returnUrl && navigate(returnUrl);

			enableSuccessAlert &&
				setAlert &&
				setAlert(
					true,
					customSuccessMessage ?? 'Visita actualizada correctamente',
					AlertSeverityEnum.success
				);
		},
		onError: error => {
			enableErrorRedirect &&
				navigate &&
				returnUrl &&
				navigate(returnErrorUrl ?? (returnUrl || ''));

			handleAxiosError(error, customMessageErrorAlert);
		},
		onSettled: () => {
			setIsGlobalLoading(false);
		},
	});
};

export const updateVisita = async (idVisita: string, data: VisitaUpdateForm) => {
	const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
	setIsGlobalLoading(true);

	return put<ApiResponse<VisitaSaveForm>>(
		`${BASE_URL}/visitas/${idVisita}`,
		data,
		true
	);
};
